import React, { useState, useEffect } from 'react';
import { getAllTransporters, createTransporter, updateTransporter } from '../../services/firestore/transporterService';
import { getTransporterTypes } from '../../services/firestore/systemConfigService';
import { getTransportModes } from '../../services/firestore/transportModeService';
import { getNextSequenceNumber } from '../../services/firestore/sequenceService';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { PERMISSIONS } from '../../constants/permissions';

export const TransporterManagement = ({ userData }) => {
    const [transporters, setTransporters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [transporterTypes, setTransporterTypes] = useState([]);
    const [transportModes, setTransportModes] = useState([]);
    const { can } = usePermissions(userData);
    const { success, error, warning } = useToast();

    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        confirmText: 'Onayla',
        cancelText: 'Vazgeç',
        onConfirm: () => { }
    });

    const showConfirm = (config) => {
        setConfirmConfig({
            isOpen: true,
            title: config.title || 'Onay Gerekiyor',
            message: config.message || 'Bu işlemi yapmak istediğinize emin misiniz?',
            type: config.type || 'warning',
            confirmText: config.confirmText || 'Evet, Onayla',
            cancelText: config.cancelText || 'İptal',
            onConfirm: () => {
                config.onConfirm();
                setConfirmConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    // Form State
    const [formData, setFormData] = useState({
        companyName: '',
        shortName: '', // New field
        transporterType: '', // New field
        supportedTransportModes: [], // New: FTL, LTL etc.
        supplierCode: '',    // New field (auto-generated)
        taxNumber: '',
        // Structured Address
        country: 'Türkiye',
        city: '',
        district: '',
        fullAddress: '',
        // Status
        status: 'active',
        // Dynamic Contacts
        contacts: []
    });

    // Temporary state for new contact entry
    const [newContact, setNewContact] = useState({
        firstName: '',
        lastName: '',
        position: '',
        phone: '',
        email: '',
        notifyReservation: false,
        notifyVehicle: false
    });

    useEffect(() => {
        if (userData?.tenantId) {
            loadTransporters();
        }
    }, [userData?.tenantId]);

    const loadTransporters = async () => {
        setLoading(true);
        try {
            const [data, typesData, modesData] = await Promise.all([
                getAllTransporters(userData.tenantId),
                getTransporterTypes(userData.tenantId),
                getTransportModes(userData.tenantId)
            ]);
            setTransporters(data);
            setTransporterTypes(typesData);
            setTransportModes(modesData);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        // Reset form for create mode
        setFormData({
            companyName: '',
            shortName: '',
            transporterType: '',
            supportedTransportModes: [],
            supplierCode: '',
            taxNumber: '',
            country: 'Türkiye',
            city: '',
            district: '',
            fullAddress: '',
            status: 'active',
            contacts: []
        });
        setNewContact({
            firstName: '',
            lastName: '',
            position: '',
            phone: '',
            email: '',
            notifyReservation: false,
            notifyVehicle: false
        });
        setIsEditing(false);
        setEditingId(null);
        setIsModalOpen(true);
    };

    // Status Definitions (Simulating Configuration Table)
    const STATUS_OPTIONS = [
        { id: 'active', label: 'Aktif', color: 'var(--status-completed)', icon: '🟢' },
        { id: 'passive', label: 'Pasif (Arşiv)', color: 'var(--text-secondary)', icon: '⚫' },
        { id: 'blacklisted', label: 'Kara Liste', color: 'var(--status-rejected)', icon: '🔴' }
    ];

    const FILTER_OPTIONS = [
        { id: 'all', label: 'Tümü' },
        { id: 'active', label: 'Aktif' },
        { id: 'passive', label: 'Pasif' },
        { id: 'blacklisted', label: 'Kara Liste' }
    ];

    // Load data into form for editing
    const handleEdit = (transporter) => {
        // Map current state to status ID
        let currentStatus = 'active';
        if (transporter.isBlacklisted) currentStatus = 'blacklisted';
        else if (!transporter.isActive) currentStatus = 'passive';

        setFormData({
            companyName: transporter.companyName || '',
            shortName: transporter.shortName || '',
            transporterType: transporter.transporterType || '',
            supportedTransportModes: transporter.supportedTransportModes || [],
            supplierCode: transporter.supplierCode || '',
            taxNumber: transporter.taxNumber || '',
            country: transporter.country || 'Türkiye',
            city: transporter.city || '',
            district: transporter.district || '',
            fullAddress: transporter.fullAddress || '',
            contacts: transporter.contacts || [],
            status: currentStatus // Set mapped status
        });
        setIsEditing(true);
        setEditingId(transporter.id);
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddContact = () => {
        if (!newContact.firstName || !newContact.lastName || !newContact.phone) {
            error('Lütfen ad, soyad ve telefon bilgilerini giriniz.');
            return;
        }
        setFormData(prev => ({
            ...prev,
            contacts: [...prev.contacts, { ...newContact, id: Date.now() }]
        }));
        setNewContact({
            firstName: '',
            lastName: '',
            position: '',
            phone: '',
            email: '',
            notifyReservation: false,
            notifyVehicle: false
        });
    };

    const handleRemoveContact = (id) => {
        setFormData(prev => ({
            ...prev,
            contacts: prev.contacts.filter(c => c.id !== id)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.contacts.length === 0) {
            showConfirm({
                title: 'İletişim Kişisi Eksik',
                message: 'Hiçbir iletişim kişisi eklemediniz. Devam etmek istiyor musunuz?',
                onConfirm: () => processSubmit(formData)
            });
            return;
        }

        processSubmit(formData);
    };

    const processSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            // Map status back to boolean fields
            let isActive = true;
            let isBlacklisted = false;

            if (data.status === 'passive') {
                isActive = false;
            } else if (data.status === 'blacklisted') {
                isActive = false;
                isBlacklisted = true;
            }

            const payload = {
                companyName: data.companyName,
                shortName: data.shortName,
                taxNumber: data.taxNumber,
                country: data.country,
                city: data.city,
                district: data.district,
                fullAddress: data.fullAddress,
                contacts: data.contacts,
                isActive: isActive,
                isBlacklisted: isBlacklisted,
                contactPerson: data.contacts.length > 0 ? `${data.contacts[0].firstName} ${data.contacts[0].lastName}` : '',
                phone: data.contacts.length > 0 ? data.contacts[0].phone : '',
                email: data.contacts.length > 0 ? data.contacts[0].email : '',
                transporterType: data.transporterType,
                supportedTransportModes: data.supportedTransportModes,
                tenantId: userData.tenantId,
            };

            if (isEditing) {
                await updateTransporter(editingId, {
                    ...payload,
                    updatedBy: userData.fullName,
                    updatedAt: new Date()
                }, userData.tenantId);
            } else {
                let finalCode = '';
                if (data.transporterType) {
                    const typeObj = transporterTypes.find(t => t.code === data.transporterType);
                    const sequenceKey = typeObj ? typeObj.code : data.transporterType;
                    const nextSeq = await getNextSequenceNumber(userData.tenantId, sequenceKey);

                    if (nextSeq) {
                        finalCode = nextSeq;
                    } else {
                        finalCode = sequenceKey + '-' + Math.floor(1000 + Math.random() * 9000);
                    }
                }

                await createTransporter({
                    ...payload,
                    transporterType: data.transporterType,
                    supplierCode: finalCode,
                    isActive: isActive,
                    isBlacklisted: isBlacklisted,
                    createdBy: userData.fullName,
                    createdById: userData.id
                }, userData.tenantId);
            }

            setIsModalOpen(false);
            loadTransporters();
            success(isEditing ? 'Tedarikçi güncellendi.' : 'Tedarikçi oluşturuldu.');
        } catch (err) {
            console.error('Error saving transporter:', err);
            error('İşlem sırasında bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredTransporters = transporters.filter(t => {
        const matchesSearch = t.companyName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'active' && t.isActive && !t.isBlacklisted) ||
            (filterStatus === 'passive' && !t.isActive && !t.isBlacklisted) ||
            (filterStatus === 'blacklisted' && t.isBlacklisted);
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center" style={{ height: '80vh' }}>
                <div className="badge badge-pending">Veriler Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="flex justify-between items-center mb-xl">
                <div>
                    <h1 style={{ marginBottom: '4px' }}>Tedarikçi Yönetimi</h1>
                    <p className="text-secondary">Sisteme kayıtlı tüm tedarikçi firmalarını yönetin ve performanslarını takip edin.</p>
                </div>
                {can(PERMISSIONS.TRANSPORTERS, PERMISSIONS.CREATE) && (
                    <Button variant="primary" onClick={handleOpenModal} style={{ boxShadow: '0 8px 16px rgba(55, 114, 255, 0.2)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="12" y2="12"></line>
                        </svg>
                        Yeni Tedarikçi Ekle
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="card-premium glow-theme mb-lg">
                <div className="flex flex-col md:flex-row gap-md justify-between items-center">
                    <div style={{ flex: 1, position: 'relative', minWidth: '300px' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                            <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input
                            type="text"
                            className="input"
                            placeholder="Firma adı, iletişim kişisi veya telefon ile ara..."
                            style={{ paddingLeft: '40px', width: '100%' }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Status Filter Tabs */}
                    <div className="flex bg-secondary rounded-lg p-1 border border-border" style={{ gap: '4px', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        {FILTER_OPTIONS.map(opt => (
                            <button
                                key={opt.id}
                                onClick={() => setFilterStatus(opt.id)}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-all`}
                                style={{
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    background: filterStatus === opt.id ? 'var(--bg-card)' : 'transparent',
                                    color: filterStatus === opt.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                                    boxShadow: filterStatus === opt.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    fontWeight: filterStatus === opt.id ? 600 : 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Transporters Table */}
            <div className="card-premium glow-theme" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ paddingLeft: '24px' }}>Tedarikçi Detayları</th>
                                <th>Lokasyon</th>
                                <th>Ana İletişim</th>
                                <th>Durum</th>
                                <th style={{ paddingRight: '24px', textAlign: 'right', width: '120px' }}>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTransporters.length > 0 ? filteredTransporters.map(transporter => (
                                <tr key={transporter.id} style={{ opacity: transporter.isActive ? 1 : 0.6 }}>
                                    <td style={{ paddingLeft: '24px' }}>
                                        <div style={{ fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}>{transporter.companyName}</div>
                                        <div className="flex gap-xs mt-1">
                                            {transporter.supplierCode && <span className="badge badge-neutral text-xs">{transporter.supplierCode}</span>}
                                            {transporter.transporterType && <span className="badge badge-neutral text-xs">{transporter.transporterType}</span>}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{transporter.city || '-'} / {transporter.country}</div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>{transporter.district}</div>
                                    </td>
                                    <td>
                                        {transporter.contacts && transporter.contacts.length > 0 ? (
                                            <>
                                                <div style={{ fontWeight: 'var(--font-semibold)' }}>{transporter.contacts[0].firstName} {transporter.contacts[0].lastName}</div>
                                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>{transporter.contacts[0].phone}</div>
                                            </>
                                        ) : (
                                            <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Kişi Yok</span>
                                        )}
                                    </td>
                                    <td>
                                        {transporter.isBlacklisted ? (
                                            <span className="badge badge-delayed">KARA LİSTE</span>
                                        ) : transporter.isActive ? (
                                            <span className="badge badge-completed">AKTİF</span>
                                        ) : (
                                            <span className="badge" style={{ background: '#4b5563', color: 'white', border: '1px solid #374151' }}>ARŞİV</span>
                                        )}
                                    </td>
                                    <td style={{ paddingRight: '24px', textAlign: 'right', verticalAlign: 'middle' }}>
                                        {can(PERMISSIONS.TRANSPORTERS, PERMISSIONS.EDIT) && (
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                style={{
                                                    background: 'var(--bg-card)',
                                                    border: '1px solid var(--border-color)',
                                                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                                onClick={() => handleEdit(transporter)}
                                            >
                                                <span>✏️</span> Düzenle
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '48px', color: 'var(--text-secondary)' }}>
                                        {filterStatus !== 'all' ? `${FILTER_OPTIONS.find(f => f.id === filterStatus)?.label} statüsünde tedarikçi bulunamadı.` : 'Henüz tedarikçi kaydı bulunmuyor.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={isEditing ? "Tedarikçi Düzenle" : "Yeni Tedarikçi Tanımla"}
                size="xl"
            >
                <form onSubmit={handleSubmit} style={{ padding: '10px' }}>

                    {/* SECTION 1: FIRMA BILGILERI */}
                    <h3 style={{ fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '16px', color: 'var(--primary-color)' }}>Firma Bilgileri & Adres</h3>

                    {/* STATUS SELECTOR - Available in Create & Edit */}
                    <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', marginBottom: '24px', border: '1px solid var(--border-color)' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>Firma Durumu</div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                            {STATUS_OPTIONS.map(opt => {
                                const isSelected = formData.status === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, status: opt.id }))}
                                        style={{
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: `2px solid ${isSelected ? opt.color : 'var(--border-color)'}`,
                                            background: isSelected ? opt.color : 'var(--bg-card)',
                                            color: isSelected ? '#fff' : 'var(--text-secondary)',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            fontWeight: isSelected ? 600 : 500,
                                            fontSize: '0.9rem',
                                            transition: 'all 0.2s ease',
                                            opacity: isSelected ? 1 : 0.7
                                        }}
                                    >
                                        <span style={{ fontSize: '1.2rem' }}>{opt.icon}</span>
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px', textAlign: 'center' }}>
                            {formData.status === 'blacklisted' && '⚠️ Bu firma kara listeye alınacak ve yeni iş alamayacak.'}
                            {formData.status === 'passive' && '📁 Bu firma arşivlenecek ve listelerde pasif görünecek.'}
                            {formData.status === 'active' && '✅ Bu firma aktif olarak iş alabilir durumda olacak.'}
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                        <div className="form-group">
                            <label className="form-label">Firma Adı *</label>
                            <input
                                type="text"
                                name="companyName"
                                className="input"
                                required
                                placeholder="Örn: Ekol Lojistik"
                                value={formData.companyName}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Kısa Tanım (Max 10)</label>
                            <input
                                type="text"
                                name="shortName"
                                className="input"
                                maxLength="10"
                                placeholder="Örn: EKOL"
                                value={formData.shortName || ''}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Tedarikçi Türü</label>
                            <select
                                name="transporterType"
                                className="select"
                                value={formData.transporterType}
                                onChange={handleInputChange}
                            >
                                <option value="">Seçiniz</option>
                                {transporterTypes.map(type => (
                                    <option key={type.id} value={type.code}>{type.name}</option>
                                ))}
                            </select>
                            {!isEditing && <div className="text-secondary text-xs mt-1">Seçilen türe göre otomatik kod üretilecektir ({formData.transporterType ? formData.transporterType + '-XXXX' : '...'})</div>}
                        </div>

                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="form-label" style={{ color: 'var(--text-secondary)', marginBottom: '12px', display: 'block' }}>Desteklenen Nakliye Şekilleri</label>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '10px',
                                padding: '16px',
                                background: 'rgba(255, 255, 255, 0.02)',
                                borderRadius: '16px',
                                border: '1px solid var(--border-color)',
                                minHeight: '60px'
                            }}>
                                {transportModes.length === 0 && (
                                    <div className="text-secondary text-sm flex items-center gap-sm">
                                        <span>ℹ️</span> Tanımlı nakliye şekli yok. Ayarlar &gt; Nakliye Türleri kısmından ekleyiniz.
                                    </div>
                                )}
                                {transportModes.map(mode => {
                                    const isSelected = formData.supportedTransportModes.includes(mode.code);
                                    return (
                                        <button
                                            key={mode.id}
                                            type="button"
                                            onClick={() => {
                                                const current = formData.supportedTransportModes;
                                                const newModes = isSelected
                                                    ? current.filter(c => c !== mode.code)
                                                    : [...current, mode.code];
                                                setFormData({ ...formData, supportedTransportModes: newModes });
                                            }}
                                            style={{
                                                padding: '8px 16px',
                                                borderRadius: '12px',
                                                border: `1px solid ${isSelected ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                                background: isSelected ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                                                color: isSelected ? '#fff' : 'var(--text-secondary)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                fontWeight: isSelected ? 700 : 500,
                                                fontSize: '0.875rem',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                boxShadow: isSelected ? '0 0 15px rgba(59, 130, 246, 0.3)' : 'none',
                                            }}
                                        >
                                            <div style={{
                                                width: '18px',
                                                height: '18px',
                                                borderRadius: '50%',
                                                border: `2px solid ${isSelected ? 'var(--primary-color)' : 'rgba(255,255,255,0.2)'}`,
                                                background: isSelected ? 'var(--primary-color)' : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                color: 'white',
                                                transition: 'all 0.2s'
                                            }}>
                                                {isSelected ? '✓' : ''}
                                            </div>
                                            {mode.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Vergi Numarası</label>
                            <input
                                type="text"
                                name="taxNumber"
                                className="input"
                                placeholder="1234567890"
                                value={formData.taxNumber}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Ülke</label>
                            <input
                                type="text"
                                name="country"
                                className="input"
                                value={formData.country}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Şehir</label>
                            <input
                                type="text"
                                name="city"
                                className="input"
                                placeholder="Örn: İstanbul"
                                value={formData.city}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">İlçe / Mahalle</label>
                            <input
                                type="text"
                                name="district"
                                className="input"
                                placeholder="Örn: Ataşehir"
                                value={formData.district}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Açık Adres Detayı</label>
                            <textarea
                                name="fullAddress"
                                className="input"
                                placeholder="Cadde, sokak, kapı no..."
                                value={formData.fullAddress}
                                onChange={handleInputChange}
                                rows={2}
                                style={{ resize: 'none' }}
                            />
                        </div>
                    </div>

                    {/* SECTION 2: ILETISIM KISILERI */}
                    <h3 style={{ fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', marginBottom: '16px', color: 'var(--primary-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Tedarikçi Sorumlu Kişiler
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 400 }}>{formData.contacts.length} Kişi Eklendi</span>
                    </h3>

                    {/* Contact Entry Form */}
                    <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', marginBottom: '16px', border: '1px solid var(--border-color)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <input type="text" className="input" placeholder="Ad" value={newContact.firstName} onChange={e => setNewContact({ ...newContact, firstName: e.target.value })} />
                            <input type="text" className="input" placeholder="Soyad" value={newContact.lastName} onChange={e => setNewContact({ ...newContact, lastName: e.target.value })} />
                            <input type="text" className="input" placeholder="Pozisyon" value={newContact.position} onChange={e => setNewContact({ ...newContact, position: e.target.value })} />
                            <input type="tel" className="input" placeholder="Telefon" value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} />
                            <input type="email" className="input" placeholder="Email" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} style={{ gridColumn: 'span 2' }} />
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="flex gap-lg">
                                <label className="flex items-center gap-sm cursor-pointer">
                                    <input type="checkbox" checked={newContact.notifyReservation} onChange={e => setNewContact({ ...newContact, notifyReservation: e.target.checked })} style={{ accentColor: 'var(--primary-color)' }} />
                                    <span style={{ fontSize: '0.9rem' }}>📨 Rez. Saat Maili</span>
                                </label>
                                <label className="flex items-center gap-sm cursor-pointer">
                                    <input type="checkbox" checked={newContact.notifyVehicle} onChange={e => setNewContact({ ...newContact, notifyVehicle: e.target.checked })} style={{ accentColor: 'var(--primary-color)' }} />
                                    <span style={{ fontSize: '0.9rem' }}>🚚 Araç Bilgisi Maili</span>
                                </label>
                            </div>
                            <Button type="button" size="sm" variant="secondary" onClick={handleAddContact} disabled={!newContact.firstName}>
                                + Listeye Ekle
                            </Button>
                        </div>
                    </div>

                    {/* Contacts List */}
                    {formData.contacts.length > 0 && (
                        <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '24px', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                            <table className="table" style={{ fontSize: '0.85rem' }}>
                                <thead style={{ background: 'var(--bg-secondary)' }}>
                                    <tr>
                                        <th style={{ padding: '8px 12px' }}>Ad Soyad</th>
                                        <th style={{ padding: '8px 12px' }}>Pozisyon</th>
                                        <th style={{ padding: '8px 12px' }}>İletişim</th>
                                        <th style={{ padding: '8px 12px' }}>Bildirimler</th>
                                        <th style={{ padding: '8px 12px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.contacts.map((contact) => (
                                        <tr key={contact.id}>
                                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>{contact.firstName} {contact.lastName}</td>
                                            <td style={{ padding: '8px 12px' }}>{contact.position}</td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <div>{contact.phone}</div>
                                                <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>{contact.email}</div>
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <div className="flex gap-xs">
                                                    {contact.notifyReservation && <span className="badge badge-pending" style={{ padding: '2px 6px', fontSize: '10px' }}>Rez</span>}
                                                    {contact.notifyVehicle && <span className="badge badge-approved" style={{ padding: '2px 6px', fontSize: '10px' }}>Araç</span>}
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                                <button type="button" onClick={() => handleRemoveContact(contact.id)} style={{ background: 'none', border: 'none', color: 'var(--status-rejected)', cursor: 'pointer' }}>✕</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="flex gap-md justify-end pt-lg" style={{ borderTop: '1px solid var(--border-color)' }}>
                        <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>Vazgeç</Button>
                        <Button type="submit" variant="primary" loading={isSubmitting} style={{ padding: '0 32px' }}>
                            {isEditing ? 'Değişiklikleri Kaydet' : 'Firmayı Kaydet'}
                        </Button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div >
    );
};

export const getPerformanceColor = (score) => {
    if (score >= 76) return 'var(--status-completed)';
    if (score >= 51) return 'var(--status-alternative)';
    return 'var(--status-delayed)';
};

export default TransporterManagement;
